import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';

/**
 * ProfileViewHero — polished read-only hero for the "View Profile" page
 * (/app/profile/:id): a wavy gradient banner, a ringed avatar with a status
 * dot, name + degree, location, contact link, a Connection / Mutual-connection
 * stat block, a "grow your network" banner, and the primary actions.
 *
 * View-only composition — the editable hero (photo/cover upload) stays in
 * HeroIdentity (used by "My Profile"), so this can't affect that screen.
 * Styling: PrimeReact + PrimeFlex + PrimeIcons; colours via theme variables.
 */

// One stat cell (icon chip + value + label) in the stat block.
const StatCell = ({ icon, tint, iconColor, value, label, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className="flex flex-column gap-2 bg-transparent border-none p-0 text-left"
        style={{ font: 'inherit', cursor: onClick ? 'pointer' : 'default', minWidth: '5.5rem' }}
        aria-label={label}
    >
        <div className="flex align-items-center gap-2">
            <span className="flex align-items-center justify-content-center border-round-lg flex-shrink-0" style={{ width: '2rem', height: '2rem', background: tint }}>
                <i className={`pi ${icon}`} style={{ color: iconColor, fontSize: '0.9rem' }} aria-hidden="true" />
            </span>
            <span className="text-900 font-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>{value}</span>
        </div>
        <span className="text-600 text-sm">{label}</span>
    </button>
);

const ProfileViewHero = ({
    profile,
    meta = {},
    actions,
    onMutualsClick,
    fallbackColor = 'blue',
}) => {
    const navigate = useNavigate();
    const contactRef = useRef(null);
    // The subject's photo URL can point at a file the backend can't serve
    // (e.g. a stale/missing establishment asset that 500s). When the <img>
    // fails, drop it so the Avatar shows initials instead of a broken image.
    const [photoError, setPhotoError] = useState(false);
    useEffect(() => setPhotoError(false), [profile?.profile_photo_url_full]);

    if (!profile) return null;

    const photoUrl = !photoError ? profile.profile_photo_url_full : undefined;

    const initials = (profile.full_name?.split(' ').map((p) => p[0]).slice(0, 2).join('') || 'U').toUpperCase();
    const location = [profile.location_city, profile.location_state, profile.location_country].filter(Boolean).join(', ');
    const mutualCount = Number(meta.mutual_count) || 0;
    const connectionCount = Number(profile.connection_count) || 0;
    const degree = meta.connection_status === 'accepted' ? '1st' : (mutualCount > 0 ? '2nd' : null);
    const hasContact = !!(profile.email || profile.phone || profile.linkedin_profile_url);
    // Green presence/open-to-work dot on the avatar.
    const showStatusDot = !!profile.open_to_work || meta.connection_status === 'accepted';

    return (
        <div className="surface-card border-round-xl shadow-1 mb-3" style={{ overflow: 'hidden' }}>
            {/* ── Decorative wavy gradient banner ─────────────────────────── */}
            <div
                aria-hidden="true"
                style={{
                    position: 'relative',
                    height: '104px',
                    background: 'linear-gradient(110deg, var(--blue-200) 0%, var(--indigo-200) 48%, var(--purple-200) 100%)',
                    overflow: 'hidden',
                }}
            >
                {/* faint dot grid, top-right */}
                <svg width="220" height="120" viewBox="0 0 220 120" style={{ position: 'absolute', top: 12, right: 24, opacity: 0.5 }}>
                    {Array.from({ length: 6 }).map((_, r) =>
                        Array.from({ length: 11 }).map((_, c) => (
                            <circle key={`${r}-${c}`} cx={c * 20 + 2} cy={r * 20 + 2} r="1.6" fill="rgba(255,255,255,0.85)" />
                        )),
                    )}
                </svg>
                {/* soft wave lines */}
                <svg viewBox="0 0 1440 150" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <path d="M0,70 C300,20 700,120 1440,40" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                    <path d="M0,110 C400,60 900,150 1440,80" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
                </svg>
                {/* bottom white wave blending into the card */}
                <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ position: 'absolute', bottom: -1, left: 0, width: '100%', height: '46px' }}>
                    <path d="M0,64 C360,120 1080,10 1440,70 L1440,120 L0,120 Z" fill="var(--surface-0)" />
                </svg>
            </div>

            {/* ── Body ────────────────────────────────────────────────────── */}
            <div className="px-3 md:px-4 pb-3 md:pb-4">
                {/* Avatar + identity (left) and stat block (right). Only the
                    avatar is pulled up to overlap the banner; name + stats stay
                    in normal flow so they're never hidden under it. */}
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3">
                    <div className="flex gap-3">
                        <div style={{ position: 'relative', width: 'fit-content', marginTop: '-3rem' }}>
                            <Avatar
                                label={initials}
                                image={photoUrl}
                                onImageError={() => setPhotoError(true)}
                                shape="circle"
                                style={{
                                    width: '7rem',
                                    height: '7rem',
                                    minWidth: '7rem',
                                    fontSize: '2rem',
                                    fontWeight: 700,
                                    backgroundColor: `var(--${fallbackColor}-100)`,
                                    color: `var(--${fallbackColor}-700)`,
                                    border: '5px solid var(--surface-0)',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    boxSizing: 'border-box',
                                    overflow: 'hidden',
                                    padding: 0,
                                    aspectRatio: '1 / 1',
                                }}
                            />
                            {showStatusDot && (
                                <span
                                    aria-hidden="true"
                                    style={{
                                        position: 'absolute', right: '0.4rem', bottom: '0.4rem',
                                        width: '1.15rem', height: '1.15rem', borderRadius: '50%',
                                        background: 'var(--green-500)', border: '3px solid var(--surface-0)',
                                    }}
                                />
                            )}
                        </div>

                        <div className="pt-2" style={{ minWidth: 0 }}>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <h1 className="text-xl font-semibold text-900 m-0">{profile.full_name || 'Unnamed profile'}</h1>
                                {profile.pronouns && <span className="text-500 text-sm">{profile.pronouns}</span>}
                                {degree && (
                                    <span className="text-primary font-medium text-xs px-2 py-1 border-round" style={{ background: 'var(--primary-50)' }}>{degree}</span>
                                )}
                                {profile.is_verified && (
                                    <i className="pi pi-verified text-primary" style={{ fontSize: '1rem' }} title="Verified" aria-label="Verified" />
                                )}
                            </div>
                            {location && (
                                <div className="flex align-items-center gap-2 text-700 text-sm mt-2">
                                    <i className="pi pi-map-marker text-primary" style={{ fontSize: '0.9rem' }} aria-hidden="true" />
                                    <span>{location}</span>
                                </div>
                            )}
                            {hasContact && (
                                <button
                                    type="button"
                                    onClick={(e) => contactRef.current?.toggle(e)}
                                    className="bg-transparent border-none p-0 mt-2 cursor-pointer text-primary font-medium text-sm flex align-items-center gap-1 hover:underline"
                                    style={{ font: 'inherit' }}
                                >
                                    Contact info <i className="pi pi-angle-right" style={{ fontSize: '0.8rem' }} aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Stat block — Connection / Mutual connections */}
                    <div
                        className="surface-50 border-round-xl p-3 flex align-items-center gap-3 flex-shrink-0"
                        style={{ border: '1px solid var(--surface-border)' }}
                    >
                        <StatCell
                            icon="pi-users"
                            tint="var(--primary-100)"
                            iconColor="var(--primary-color)"
                            value={connectionCount.toLocaleString('en-IN')}
                            label={connectionCount === 1 ? 'Connection' : 'Connections'}
                        />
                        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--surface-border)' }} aria-hidden="true" />
                        <StatCell
                            icon="pi-users"
                            tint="var(--green-100)"
                            iconColor="var(--green-600)"
                            value={mutualCount.toLocaleString('en-IN')}
                            label="Mutual connections"
                            onClick={mutualCount > 0 ? onMutualsClick : undefined}
                        />
                    </div>
                </div>

                {/* Grow-your-network banner */}
                <div className="surface-ground border-round-xl p-3 mt-3 flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                    <div className="flex align-items-center gap-3">
                        <span className="flex align-items-center justify-content-center border-circle flex-shrink-0" style={{ width: '2.75rem', height: '2.75rem', background: 'var(--primary-100)' }}>
                            <i className="pi pi-users text-primary" style={{ fontSize: '1.1rem' }} aria-hidden="true" />
                        </span>
                        <div className="flex flex-column">
                            <span className="text-900 font-semibold text-sm">Let’s grow your network</span>
                            <span className="text-600 text-sm">Connect with professionals and explore new opportunities.</span>
                        </div>
                    </div>
                    <Button
                        label="View connections"
                        icon="pi pi-users"
                        outlined
                        size="small"
                        onClick={() => navigate('/app/my-network')}
                        className="w-full md:w-auto"
                    />
                </div>

                {/* Actions — Message / More actions (from parent) */}
                {actions && (
                    <div className="flex flex-wrap gap-2 mt-3">{actions}</div>
                )}
            </div>

            {/* Contact info overlay */}
            <OverlayPanel ref={contactRef} className="w-18rem" dismissable>
                <div className="flex flex-column gap-3">
                    <span className="text-900 font-semibold text-sm">Contact info</span>
                    {profile.email && (
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-envelope text-primary" style={{ fontSize: '0.9rem' }} aria-hidden="true" />
                            <a href={`mailto:${profile.email}`} className="text-sm text-700 no-underline hover:underline" style={{ wordBreak: 'break-all' }}>{profile.email}</a>
                        </div>
                    )}
                    {profile.phone && (
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-phone text-primary" style={{ fontSize: '0.9rem' }} aria-hidden="true" />
                            <a href={`tel:${profile.phone}`} className="text-sm text-700 no-underline hover:underline">{profile.phone}</a>
                        </div>
                    )}
                    {profile.linkedin_profile_url && (
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-linkedin text-primary" style={{ fontSize: '0.9rem' }} aria-hidden="true" />
                            <a href={profile.linkedin_profile_url} target="_blank" rel="noopener noreferrer" className="text-sm text-700 no-underline hover:underline" style={{ wordBreak: 'break-all' }}>LinkedIn profile</a>
                        </div>
                    )}
                </div>
            </OverlayPanel>
        </div>
    );
};

export default ProfileViewHero;
